import React, { useState, useEffect, useCallback, useRef } from "react";
import { useCampaignStore } from "../../store/useCampaignStore";
import { useBikeStore } from "../../store/useBikeStore";
import { useCityStore } from "../../store/useCityStore";
import { useImpressionJobStore } from "../../store/useImpressionJobStore";
import { useAppNavigate } from "../../hooks/useAppNavigate";
import type { Campaign } from "../../types/campaign";
import { LoadingSpinner } from "../common/LoadingSpinner";
import { CampaignFilters } from "./CampaignFilters";
import { CampaignsTable } from "./CampaignsTable";
import { CampaignPagination } from "./CampaignPagination";
import { CampaignModal } from "./CampaignModal";
import { Plus, RefreshCw } from "lucide-react";

export const CampaignsList: React.FC = () => {
  const navigateToView = useAppNavigate();
  const {
    campaigns,
    loading,
    totalCount,
    currentPage,
    totalPages,
    itemsPerPage,
    fetchCampaigns,
    addCampaign,
    updateCampaign,
    removeCampaign,
  } = useCampaignStore();

  const { loadIfNeeded: loadBikesIfNeeded } = useBikeStore();
  const { loadIfNeeded: loadCitiesIfNeeded, cities } = useCityStore();
  const { createJob } = useImpressionJobStore();

  // Local state for filters, pagination, and UI
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [cityFilter, setCityFilter] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("start_date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit" | "delete">(
    "create",
  );
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(
    null,
  );

  // Debounce timer ref
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Load bikes and cities for modal
  useEffect(() => {
    loadBikesIfNeeded().catch(console.error);
    loadCitiesIfNeeded().catch(console.error);
  }, [loadBikesIfNeeded, loadCitiesIfNeeded]);

  // Debounce search input
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [search]);

  // Fetch campaigns with current filters and page
  const fetchCampaignsWithFilters = useCallback(
    (page: number) => {
      const params: Record<string, unknown> = {
        page,
        limit: itemsPerPage,
      };

      if (debouncedSearch) {
        params.search = debouncedSearch;
      }
      if (statusFilter) {
        params.status = statusFilter;
      }
      if (cityFilter) {
        params.city_id = cityFilter;
      }
      if (sortBy) {
        params.sort_by = sortBy;
        params.sort_order = sortOrder;
      }

      fetchCampaigns(params);
    },
    [
      itemsPerPage,
      debouncedSearch,
      statusFilter,
      cityFilter,
      sortBy,
      sortOrder,
      fetchCampaigns,
    ],
  );

  // Fetch campaigns on mount and when filters change
  useEffect(() => {
    fetchCampaignsWithFilters(1);
  }, [debouncedSearch, statusFilter, cityFilter, sortBy, sortOrder]);

  // Handle page change
  const handlePageChange = (page: number) => {
    fetchCampaignsWithFilters(page);
  };

  // Handle sort change
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  // Handle create campaign
  const handleCreate = () => {
    setSelectedCampaign(null);
    setModalMode("create");
    setModalOpen(true);
  };

  // Handle edit campaign
  const handleEdit = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setModalMode("edit");
    setModalOpen(true);
  };

  // Handle delete campaign
  const handleDelete = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setModalMode("delete");
    setModalOpen(true);
  };

  // Handle save (create or update)
  const handleSave = async (campaignData: Record<string, unknown>) => {
    if (modalMode === "create") {
      await addCampaign(
        campaignData as Omit<Campaign, "created_at" | "updated_at">,
      );
    } else if (modalMode === "edit" && selectedCampaign) {
      await updateCampaign(
        selectedCampaign.campaign_id,
        campaignData as Partial<
          Pick<Campaign, "start_date" | "end_date" | "bike_ids" | "city_ids">
        >,
      );
    } else if (modalMode === "delete" && selectedCampaign) {
      await removeCampaign(selectedCampaign.campaign_id);
    }

    setModalOpen(false);
    fetchCampaignsWithFilters(currentPage);
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchCampaignsWithFilters(currentPage);
  };

  // Handle calculate impressions
  const handleCalculateImpressions = async (campaign: Campaign) => {
    try {
      await createJob(campaign.campaign_id);
      // Navigate to impression jobs list
      navigateToView("impression-jobs");
    } catch (error) {
      console.error("Error creating impression job:", error);
      alert("Failed to create impression job. Please try again.");
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Campaigns</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage advertising campaigns with bike and city assignments
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleRefresh}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center space-x-2"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            <span>Refresh</span>
          </button>
          <button
            onClick={handleCreate}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Campaign
          </button>
        </div>
      </div>

      {/* Filters */}
      <CampaignFilters
        search={search}
        onSearchChange={setSearch}
        status={statusFilter}
        onStatusChange={setStatusFilter}
        city={cityFilter}
        onCityChange={setCityFilter}
        cities={cities}
      />

      {/* Table */}
      {loading && campaigns.length === 0 ? (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          <CampaignsTable
            campaigns={campaigns}
            cities={cities}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onCalculateImpressions={handleCalculateImpressions}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <CampaignPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}

      {/* Modal */}
      <CampaignModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
        campaign={selectedCampaign}
        onSave={handleSave}
      />
    </div>
  );
};
